import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { useMutation, useApolloClient } from '@apollo/client';
import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik';
import { z } from 'zod';
import yaml from 'js-yaml';
import { useAuth } from '@clerk/clerk-react';
import { useAtom, useSetAtom } from 'jotai';
import { useNavigate } from '@tanstack/react-router';
import {
  CREATE_PORTFOLIO,
  GET_PORTFOLIO_BY_USER_ID,
  GET_ALL_PORTFOLIOS,
  GET_PORTFOLIO_NAME,
  PortfolioSetInput,
} from '~/graphql/queries/portfolio';
import { portfolioIdAtom, latestSnapshotIdAtom } from '~/atoms/dashboardAtom';
import { toastAtom } from '~/atoms/toastAtom';
import { Toast } from 'primereact/toast';
import { currencyList } from '~/config/currenciesStatic';
import { isValidISIN } from '~/utils/validation';
import { AllocationAnalysisDialog } from '~/components/ui/widgets/AllocationWidget/AllocationAnalysisDialog';
import { endpointPortfolioAnalysisPOST } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { endpointAllocationBatchUploadPOST } from '~/api/def/endpoints/allocationBatchEndpoint';
import { endpointPortfolioReAnalysisPOST } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { Checkbox } from 'primereact/checkbox';
import { Card } from 'primereact/card';

interface FormValues {
  fundName: string;
  fundCode: string;
  fundAum: string;
  fundCurrency: string;
  inceptionDate: Date | null;
  benchmarkIsin: string;
  investmentGuidelines: string;
}

const validCurrencyCodes = currencyList.map((currency) => currency.value);

const validationSchema = z.object({
  fundName: z
    .string()
    .min(2, 'Fund name must be at least 2 characters')
    .max(100, 'Fund name must be less than 100 characters'),
  fundCode: z
    .string()
    .min(2, 'Fund code must be at least 2 characters')
    .max(20, 'Fund code must be less than 20 characters'),
  fundAum: z
    .string()
    .optional()
    .refine((value) => {
      if (!value) return true;
      return !isNaN(Number(value.replace(/,/g, '')));
    }, 'AUM must be a valid number'),
  fundCurrency: z.string().refine((val) => validCurrencyCodes.includes(val), {
    message: 'Please select a valid currency',
  }),
  inceptionDate: z
    .date()
    .nullable()
    .optional()
    .refine((date) => {
      if (!date) return true;
      return date <= new Date();
    }, 'Inception date cannot be in the future'),
  benchmarkIsin: z
    .string()
    .optional()
    .refine((value) => {
      if (!value || value.trim() === '') return true;
      return isValidISIN(value);
    }, 'Invalid ISIN. Must be 12 characters with a valid check digit (e.g. US0378331005)'),
  investmentGuidelines: z.string().optional(),
});

const validateForm = (values: FormValues) => {
  try {
    validationSchema.parse(values);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldErrors: Record<string, string> = {};
      error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      return fieldErrors;
    }
    return {};
  }
};

interface CreatePortfolioStepperDialogProps {
  visible: boolean;
  onHide: () => void;
}

export const CreatePortfolioStepperDialog: React.FC<
  CreatePortfolioStepperDialogProps
> = ({ visible, onHide }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [createdPortfolioId, setCreatedPortfolioId] = useState<string | null>(
    null,
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const setPortfolioId = useSetAtom(portfolioIdAtom);
  const setLatestSnapshotId = useSetAtom(latestSnapshotIdAtom);
  const [showUniverseDialog, setShowUniverseDialog] = useState(false);
  const [showCurrentHoldingsDialog, setShowCurrentHoldingsDialog] =
    useState(false);
  const [universeUploaded, setUniverseUploaded] = useState(false);
  const [currentHoldingsUploaded, setCurrentHoldingsUploaded] = useState(false);
  const [runAnalysis, setRunAnalysis] = useState(true);
  const [snapshotId, setSnapshotId] = useState<string | null>(null);

  // Use Clerk orgId directly as companyId
  const companyId = auth.orgId;
  const apolloClient = useApolloClient();

  // Reset state when dialog is opened
  useEffect(() => {
    if (visible) {
      setActiveStep(0);
      setCreatedPortfolioId(null);
      setUploadError(null);
      setUniverseUploaded(false);
      setCurrentHoldingsUploaded(false);
      setSnapshotId(null);
      setShowUniverseDialog(false);
      setShowCurrentHoldingsDialog(false);
      setRunAnalysis(true);
      setIsUploading(false);
    }
  }, [visible]);

  const [createPortfolio, { loading: createLoading }] = useMutation(
    CREATE_PORTFOLIO,
    {
      refetchQueries: [
        { query: GET_PORTFOLIO_BY_USER_ID },
        { query: GET_ALL_PORTFOLIOS },
      ],
      onCompleted: async (data) => {
        const portfolioId = data.insert_EQPortfolio_one?.id;
        if (portfolioId) {
          setCreatedPortfolioId(portfolioId);
          setActiveStep(1);

          // Refetch portfolio queries to update the sidebar list
          try {
            await apolloClient.refetchQueries({
              include: [GET_PORTFOLIO_BY_USER_ID, GET_ALL_PORTFOLIOS],
            });
          } catch (refetchError) {
            console.error('Error refetching portfolios:', refetchError);
            // Continue even if refetch fails - portfolio was created successfully
          }

          toastCurrent?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Portfolio created successfully',
          });
        } else {
          // Edge case: Portfolio creation succeeded but no ID returned
          toastCurrent?.show({
            severity: 'error',
            summary: 'Error',
            detail:
              'Portfolio created but ID not returned. Please refresh the page.',
          });
        }
      },
      onError: (error) => {
        console.error('Error creating portfolio:', error);
        toastCurrent?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create portfolio. Please try again.',
        });
      },
    },
  );

  const initialValues: FormValues = {
    fundName: '',
    fundCode: '',
    fundAum: '',
    fundCurrency: 'CHF',
    inceptionDate: null,
    benchmarkIsin: '',
    investmentGuidelines: '',
  };

  const handleStep1Submit = async (values: FormValues) => {
    try {
      // Parse investment guidelines YAML to object
      let fundType: object | null = null;
      if (values.investmentGuidelines.trim()) {
        try {
          fundType = yaml.load(values.investmentGuidelines) as object;
        } catch (yamlError) {
          toastCurrent?.show({
            severity: 'error',
            summary: 'Invalid YAML',
            detail: 'Investment guidelines must be valid YAML format',
          });
          return;
        }
      }

      const portfolioInput: PortfolioSetInput & {
        user_id?: string;
        companyId?: string;
      } = {
        name: values.fundName,
        fund_code: values.fundCode,
        base_currency: values.fundCurrency,
        inception_date: values.inceptionDate
          ? values.inceptionDate.toISOString().split('T')[0]
          : null,
        aum: values.fundAum
          ? parseFloat(values.fundAum.replace(/,/g, ''))
          : null,
        fund_type: fundType,
        benchmark_index: values.benchmarkIsin.trim().toUpperCase() || null,
        status: 'active',
      };

      // Add companyId (required) - use Clerk orgId directly
      if (companyId) {
        portfolioInput.companyId = companyId;
      } else {
        toastCurrent?.show({
          severity: 'error',
          summary: 'Error',
          detail:
            'Organization ID not found. Please ensure you are part of an organization.',
        });
        return;
      }

      // Add user_id for portfolio owners
      if (auth.userId) {
        portfolioInput.user_id = auth.userId;
      }

      await createPortfolio({
        variables: {
          portfolio: portfolioInput,
        },
      });
    } catch (error) {
      console.error('Error in handleStep1Submit:', error);
    }
  };

  const handleUniverseUpload = async (
    csvData: Array<{
      isin: string;
      current_allocation: number;
      sector: string;
      ticker?: string;
      name?: string;
    }>,
    description: string,
  ) => {
    if (!createdPortfolioId) {
      toastCurrent?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Portfolio ID not available.',
      });
      return;
    }

    try {
      setIsUploading(true);

      // If no snapshot exists yet, create one with empty allocations
      let currentSnapshotId = snapshotId;
      if (!currentSnapshotId) {
        const snapshotResponse = await endpointPortfolioAnalysisPOST({
          portfolio_id: createdPortfolioId,
          allocations: [],
          description: 'Initial snapshot for reference universe',
          snapshot_type: 'manual',
        });

        if (snapshotResponse && snapshotResponse.snapshot_id) {
          currentSnapshotId = snapshotResponse.snapshot_id;
          setSnapshotId(currentSnapshotId);
          setLatestSnapshotId(currentSnapshotId);
        } else {
          toastCurrent?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create snapshot. Please try again.',
          });
          return;
        }
      }

      const allocations = csvData.map((item) => ({
        isin: item.isin,
        allocation_percent: item.current_allocation,
      }));

      const batchResponse = await endpointAllocationBatchUploadPOST(
        currentSnapshotId,
        {
          allocations,
          description: description || undefined,
          batch_type: 'universe',
        },
      );

      if (batchResponse) {
        // Update snapshot ID if returned in response
        if (batchResponse.snapshot_id) {
          setSnapshotId(batchResponse.snapshot_id);
          setLatestSnapshotId(batchResponse.snapshot_id);
        }
        setUniverseUploaded(true);
        toastCurrent?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Successfully uploaded ${batchResponse.created_allocations || 0} reference universe allocations.`,
        });
        setShowUniverseDialog(false);
      } else {
        // Edge case: Upload succeeded but no response
        toastCurrent?.show({
          severity: 'warn',
          summary: 'Warning',
          detail:
            'Upload completed but no confirmation received. Please verify the upload.',
        });
      }
    } catch (error) {
      console.error('Error uploading universe:', error);
      toastCurrent?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to upload reference universe. Please try again.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCurrentHoldingsUpload = async (
    csvData: Array<{
      isin: string;
      current_allocation: number;
      sector: string;
      ticker?: string;
      name?: string;
    }>,
    description: string,
  ) => {
    if (!createdPortfolioId) {
      toastCurrent?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Portfolio ID not available.',
      });
      return;
    }

    try {
      setIsUploading(true);
      const allocations = csvData.map((item) => ({
        isin: item.isin,
        current_allocation: item.current_allocation,
        allocation_percent: item.current_allocation,
      }));

      const snapshotResponse = await endpointPortfolioAnalysisPOST({
        portfolio_id: createdPortfolioId,
        allocations,
        description: description || 'Manual upload',
        snapshot_type: 'manual',
      });

      if (snapshotResponse && snapshotResponse.snapshot_id) {
        setSnapshotId(snapshotResponse.snapshot_id);
        setLatestSnapshotId(snapshotResponse.snapshot_id);
        setCurrentHoldingsUploaded(true);
        toastCurrent?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Successfully created snapshot with ${snapshotResponse.created_allocations} allocations.`,
        });
        setShowCurrentHoldingsDialog(false);
        // Note: Analysis will run when user clicks "Finish" if checkbox is checked
      } else {
        // Edge case: Upload succeeded but no snapshot_id returned
        toastCurrent?.show({
          severity: 'warn',
          summary: 'Warning',
          detail:
            'Upload completed but snapshot ID not returned. You may need to re-upload.',
        });
      }
    } catch (error) {
      console.error('Error uploading current holdings:', error);
      toastCurrent?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to upload current holdings. Please try again.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFinish = async () => {
    // Edge case: If no portfolio was created, don't proceed
    if (!createdPortfolioId) {
      toastCurrent?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No portfolio was created. Please create a portfolio first.',
      });
      return;
    }

    // Run analysis if checkbox is checked and we have a snapshot
    // Analysis requires reference universe (which creates/uses a snapshot)
    if (runAnalysis && universeUploaded && snapshotId && createdPortfolioId) {
      try {
        setIsUploading(true);
        await endpointPortfolioReAnalysisPOST({
          portfolio_id: createdPortfolioId,
          snapshot_id: snapshotId,
          force_recalculation: true,
        });
        toastCurrent?.show({
          severity: 'success',
          summary: 'Analysis Started',
          detail:
            'Portfolio analysis has been started. Results will be available shortly.',
        });
      } catch (analysisError) {
        console.error('Error starting analysis:', analysisError);
        toastCurrent?.show({
          severity: 'warn',
          summary: 'Analysis Not Started',
          detail:
            'Reference universe uploaded but analysis could not be started. You can run it manually later.',
        });
      } finally {
        setIsUploading(false);
      }
    }

    // Ensure portfolio is selected before refetching
    setPortfolioId(createdPortfolioId);

    // Refetch all portfolio queries to update the UI and sidebar
    try {
      await apolloClient.refetchQueries({
        include: [
          GET_PORTFOLIO_BY_USER_ID,
          GET_ALL_PORTFOLIOS,
          GET_PORTFOLIO_NAME,
        ],
      });
    } catch (refetchError) {
      console.error('Error refetching portfolios:', refetchError);
      // Continue even if refetch fails - portfolio selection should still work
    }

    // Reset state before closing
    setActiveStep(0);
    setCreatedPortfolioId(null);
    setUploadError(null);
    setUniverseUploaded(false);
    setCurrentHoldingsUploaded(false);
    setSnapshotId(null);
    setShowUniverseDialog(false);
    setShowCurrentHoldingsDialog(false);
    setRunAnalysis(true);

    // Close the dialog
    onHide();

    // Navigate to the portfolio overview page
    if (createdPortfolioId) {
      navigate({
        to: '/portfolio/$portfolioId/overview',
        params: { portfolioId: createdPortfolioId },
      });
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setCreatedPortfolioId(null);
    setUploadError(null);
    setUniverseUploaded(false);
    setCurrentHoldingsUploaded(false);
    setSnapshotId(null);
    setShowUniverseDialog(false);
    setShowCurrentHoldingsDialog(false);
    onHide();
  };

  const steps = [
    { label: 'Create Portfolio', index: 0 },
    { label: 'Upload Allocations', index: 1 },
  ];

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      header="Create New Portfolio"
      style={{ width: '90vw', maxWidth: '800px' }}
      modal
      className="p-fluid"
      closable={!isUploading && !createLoading}
    >
      {/* Stepper Header */}
      <div className="flex align-items-center justify-content-between mb-4">
        {steps.map((step, index) => (
          <React.Fragment key={step.index}>
            <div className="flex align-items-center">
              <div
                className={`flex align-items-center justify-content-center border-circle ${
                  activeStep >= step.index
                    ? 'bg-primary text-primary-contrast'
                    : 'bg-surface border-2 border-primary'
                }`}
                style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  minWidth: '2.5rem',
                }}
              >
                {activeStep > step.index ? (
                  <i className="pi pi-check text-sm"></i>
                ) : (
                  <span className="text-sm font-semibold">
                    {step.index + 1}
                  </span>
                )}
              </div>
              <span
                className={`ml-2 ${
                  activeStep >= step.index ? 'text-primary' : 'text-500'
                } font-medium`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`flex-1 mx-3 border-top-2 ${
                  activeStep > step.index ? 'border-primary' : 'border-300'
                }`}
                style={{ height: '2px' }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Create Portfolio */}
      {activeStep === 0 && (
        <Formik
          initialValues={initialValues}
          validate={validateForm}
          onSubmit={handleStep1Submit}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            isSubmitting,
          }: FormikProps<FormValues>) => (
            <Form>
              <div className="flex flex-column gap-3">
                <div className="field">
                  <label htmlFor="fundName" className="font-medium">
                    Fund Name <span className="text-red-500">*</span>
                  </label>
                  <Field
                    as={InputText}
                    id="fundName"
                    name="fundName"
                    placeholder="Enter fund name"
                    className={
                      errors.fundName && touched.fundName ? 'p-invalid' : ''
                    }
                  />
                  <ErrorMessage
                    name="fundName"
                    component="small"
                    className="p-error"
                  />
                </div>

                <div className="field">
                  <label htmlFor="fundCode" className="font-medium">
                    Fund Code <span className="text-red-500">*</span>
                  </label>
                  <Field
                    as={InputText}
                    id="fundCode"
                    name="fundCode"
                    placeholder="Enter fund code"
                    className={
                      errors.fundCode && touched.fundCode ? 'p-invalid' : ''
                    }
                  />
                  <ErrorMessage
                    name="fundCode"
                    component="small"
                    className="p-error"
                  />
                </div>

                <div className="flex gap-3">
                  <div className="field flex-1">
                    <label htmlFor="fundCurrency" className="font-medium">
                      Currency <span className="text-red-500">*</span>
                    </label>
                    <Field
                      as={Dropdown}
                      id="fundCurrency"
                      name="fundCurrency"
                      options={currencyList}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select currency"
                      className={
                        errors.fundCurrency && touched.fundCurrency
                          ? 'p-invalid'
                          : ''
                      }
                    />
                    <ErrorMessage
                      name="fundCurrency"
                      component="small"
                      className="p-error"
                    />
                  </div>

                  <div className="field flex-1">
                    <label htmlFor="fundAum" className="font-medium">
                      AUM
                    </label>
                    <Field
                      as={InputText}
                      id="fundAum"
                      name="fundAum"
                      placeholder="Enter AUM"
                      className={
                        errors.fundAum && touched.fundAum ? 'p-invalid' : ''
                      }
                    />
                    <ErrorMessage
                      name="fundAum"
                      component="small"
                      className="p-error"
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="inceptionDate" className="font-medium">
                    Inception Date
                  </label>
                  <Field
                    as={Calendar}
                    id="inceptionDate"
                    name="inceptionDate"
                    value={values.inceptionDate}
                    onChange={(e: { value: Date | null }) =>
                      setFieldValue('inceptionDate', e.value)
                    }
                    dateFormat="yy-mm-dd"
                    showIcon
                    maxDate={new Date()}
                    className={
                      errors.inceptionDate && touched.inceptionDate
                        ? 'p-invalid'
                        : ''
                    }
                  />
                  <ErrorMessage
                    name="inceptionDate"
                    component="small"
                    className="p-error"
                  />
                </div>

                <div className="field">
                  <label htmlFor="benchmarkIsin" className="font-medium">
                    Benchmark ISIN
                  </label>
                  <Field
                    as={InputText}
                    id="benchmarkIsin"
                    name="benchmarkIsin"
                    placeholder="e.g. US0378331005"
                    className={
                      errors.benchmarkIsin && touched.benchmarkIsin
                        ? 'p-invalid'
                        : ''
                    }
                  />
                  <small className="text-500">
                    Optional: ISIN of the benchmark index
                  </small>
                  <ErrorMessage
                    name="benchmarkIsin"
                    component="small"
                    className="p-error"
                  />
                </div>

                <div className="field">
                  <label htmlFor="investmentGuidelines" className="font-medium">
                    Investment Guidelines (YAML)
                  </label>
                  <Field
                    as={InputTextarea}
                    id="investmentGuidelines"
                    name="investmentGuidelines"
                    placeholder="Enter investment guidelines in YAML format"
                    rows={5}
                    className={
                      errors.investmentGuidelines &&
                      touched.investmentGuidelines
                        ? 'p-invalid'
                        : ''
                    }
                  />
                  <small className="text-500">
                    Optional: Enter investment guidelines in YAML format
                  </small>
                  <ErrorMessage
                    name="investmentGuidelines"
                    component="small"
                    className="p-error"
                  />
                </div>
              </div>

              <div className="flex justify-content-end gap-2 mt-4">
                <Button
                  type="button"
                  label="Cancel"
                  icon="pi pi-times"
                  outlined
                  onClick={handleClose}
                />
                <Button
                  type="submit"
                  label="Create Portfolio"
                  icon="pi pi-check"
                  loading={createLoading || isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}

      {/* Step 2: Upload Allocations */}
      {activeStep === 1 && (
        <div className="flex flex-column gap-3">
          <p className="text-600 mb-3">
            Upload your portfolio allocations. Reference universe is required to
            run portfolio analysis automatically. Current holdings is optional.
          </p>

          {/* Reference Universe Upload */}
          <Card
            className="mb-3"
            pt={{
              root: {
                style: {
                  borderRadius: '6px',
                  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.15)',
                  border: '1px solid var(--bq-border-muted)',
                },
              },
              body: {
                style: {
                  backgroundColor:
                    'var(--bq-surface-body, var(--bq-color-gray-light2, #141414))',
                  borderRadius: '6px',
                  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.15)',
                },
              },
            }}
          >
            <div className="flex flex-column gap-3">
              <div className="flex align-items-center justify-content-between">
                <div>
                  <h4
                    className="m-0 mb-2"
                    style={{ fontSize: '1.125rem', fontWeight: 600 }}
                  >
                    Reference Universe <span className="text-red-500">*</span>
                  </h4>
                  <p className="text-500 text-sm m-0">
                    Upload your reference universe allocations for comparison.
                    Required to run portfolio analysis automatically.
                  </p>
                </div>
                <div className="flex align-items-center gap-3">
                  {universeUploaded && (
                    <i className="pi pi-check-circle text-green-500 text-xl"></i>
                  )}
                  <Button
                    label={universeUploaded ? 'Re-upload' : 'Upload'}
                    icon={universeUploaded ? 'pi pi-refresh' : 'pi pi-upload'}
                    onClick={() => setShowUniverseDialog(true)}
                    outlined={universeUploaded}
                    className="w-8rem"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Current Holdings Upload */}
          <Card
            className="mb-3"
            pt={{
              root: {
                style: {
                  borderRadius: '6px',
                  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.15)',
                  border: '1px solid var(--bq-border-muted)',
                },
              },
              body: {
                style: {
                  backgroundColor:
                    'var(--bq-surface-body, var(--bq-color-gray-light2, #141414))',
                  borderRadius: '6px',
                  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.15)',
                },
              },
            }}
          >
            <div className="flex flex-column gap-3">
              <div className="flex align-items-center justify-content-between">
                <div>
                  <h4
                    className="m-0 mb-2"
                    style={{ fontSize: '1.125rem', fontWeight: 600 }}
                  >
                    Current Holdings
                  </h4>
                  <p className="text-500 text-sm m-0">
                    Upload your current portfolio holdings to create a snapshot.
                    Optional - you can upload this later.
                  </p>
                </div>
                <div className="flex align-items-center gap-3">
                  {currentHoldingsUploaded && (
                    <i className="pi pi-check-circle text-green-500 text-xl"></i>
                  )}
                  <Button
                    label={currentHoldingsUploaded ? 'Re-upload' : 'Upload'}
                    icon={
                      currentHoldingsUploaded ? 'pi pi-refresh' : 'pi pi-upload'
                    }
                    onClick={() => setShowCurrentHoldingsDialog(true)}
                    outlined={currentHoldingsUploaded}
                    className="w-8rem"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Run Analysis Option */}
          {universeUploaded && (
            <Card
              className="mb-3"
              pt={{
                root: {
                  style: {
                    borderRadius: '6px',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.15)',
                    border: '1px solid var(--bq-border-muted)',
                  },
                },
                body: {
                  style: {
                    backgroundColor:
                      'var(--bq-surface-body, var(--bq-color-gray-light2, #141414))',
                    borderRadius: '6px',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.15)',
                  },
                },
              }}
            >
              <div className="flex align-items-center gap-2">
                <Checkbox
                  inputId="runAnalysis"
                  checked={runAnalysis}
                  onChange={(e) => setRunAnalysis(e.checked ?? false)}
                />
                <label htmlFor="runAnalysis" className="cursor-pointer">
                  Run portfolio analysis automatically after upload
                </label>
              </div>
              <p className="text-500 text-sm mt-2 mb-0">
                When enabled, analysis will start automatically after reference
                universe is uploaded. Results will be available shortly.
              </p>
            </Card>
          )}

          {uploadError && (
            <Message severity="error" text={uploadError} className="mt-2" />
          )}

          {!universeUploaded && (
            <Message
              severity="info"
              text="Reference universe is required to run portfolio analysis automatically. You can upload it now or later."
              className="mt-2"
            />
          )}

          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              label="Skip"
              icon="pi pi-forward"
              outlined
              onClick={handleFinish}
              disabled={isUploading}
            />
            <Button
              type="button"
              label="Finish"
              icon="pi pi-check"
              onClick={handleFinish}
              loading={isUploading}
              disabled={isUploading}
            />
          </div>
        </div>
      )}

      {/* Allocation Analysis Dialogs */}
      <AllocationAnalysisDialog
        visible={showCurrentHoldingsDialog}
        onHide={() => setShowCurrentHoldingsDialog(false)}
        onDataProcessed={handleCurrentHoldingsUpload}
        forceCurrentHoldings={true}
        initialBatchType="current_holdings"
      />

      <AllocationAnalysisDialog
        visible={showUniverseDialog}
        onHide={() => setShowUniverseDialog(false)}
        onDataProcessed={handleUniverseUpload}
        forceCurrentHoldings={false}
        initialBatchType="universe"
      />
    </Dialog>
  );
};
